/* Copy dos modais da área Usuários (criação + ações por linha: impersonate/license/session/
   device) + catálogo de erros. Separado de `users.ts` só por causa do limite de 500 LOC (§4.7)
   — `useUsersContent()` funde os dois módulos para quem consome.

   `UserErrorKey` usa camelCase (`notFound`, `emailTaken`) em vez de espelhar o `ApiErrorCode`
   snake_case do backend: a chave vira propriedade do objeto `errors` abaixo, e
   `useNamingConvention` rejeita propriedade snake_case. `user-error.ts` faz o mapeamento via
   `switch` (mesmo idioma de `pack-submission-status.ts`). */
import type { Bilingual } from '../bilingual.js';

export type UserErrorKey =
  | 'notFound'
  | 'notBlocked'
  | 'emailTaken'
  | 'unauthorized'
  | 'network'
  | 'default'
  | 'unexpected';

export interface UsersModalsContent {
  readonly createModal: {
    readonly title: string;
    readonly description: string;
    readonly emailLabel: string;
    readonly emailPlaceholder: string;
    readonly nameLabel: string;
    readonly namePlaceholder: string;
    readonly validationError: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: (email: string) => string;
  };
  readonly cooldownModal: {
    readonly title: string;
    readonly description: (deviceIdPrefix: string) => string;
    readonly confirmLabel: string;
    readonly reasonLabel: string;
    readonly hoursLabel: string;
    readonly success: string;
  };
  readonly impersonateModal: {
    readonly title: (email: string) => string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly reasonLabel: string;
    readonly success: (email: string) => string;
    readonly copyFailed: string;
  };
  readonly changeAccessModal: {
    readonly blockTitle: (email: string) => string;
    readonly unblockTitle: (email: string) => string;
    readonly blockDescription: string;
    readonly unblockDescription: string;
    readonly blockConfirm: string;
    readonly unblockConfirm: string;
    readonly reasonLabel: string;
    readonly blockedSuccess: (revoked: number) => string;
    readonly alreadyBlocked: string;
    readonly unblockedSuccess: string;
    readonly alreadyActive: string;
  };
  readonly issueLicenseModal: {
    readonly title: string;
    readonly description: string;
    readonly packIdLabel: string;
    readonly versionLabel: string;
    readonly justificationLabel: string;
    readonly kindComp: string;
    readonly kindBeta: string;
    readonly kindInternal: string;
    readonly reasonLabel: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: (packId: string) => string;
  };
  readonly revokeLicenseModal: {
    readonly title: (packId: string) => string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly reasonLabel: string;
    readonly success: (packId: string) => string;
  };
  readonly revokeSessionModal: {
    readonly title: string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly reasonLabel: string;
    readonly success: string;
  };
  readonly transferLicenseModal: {
    readonly title: (packId: string) => string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly reasonLabel: string;
    readonly destinationLabel: string;
    readonly success: (packId: string, email: string) => string;
  };
  readonly errors: Readonly<Record<UserErrorKey, string>>;
}

export const USERS_MODALS_CONTENT: Bilingual<UsersModalsContent> = {
  'pt-BR': {
    createModal: {
      title: 'Novo usuário',
      description:
        'Cria uma conta de usuário do app já verificada. Não há senha: o usuário faz login pelo ' +
        'app com um código enviado por e-mail (OTP).',
      emailLabel: 'E-mail',
      emailPlaceholder: 'usuario@exemplo.com',
      nameLabel: 'Nome (opcional)',
      namePlaceholder: 'Nome de exibição',
      validationError:
        'Informe um e-mail válido. O nome, se preenchido, deve ter até 120 caracteres.',
      cancel: 'Cancelar',
      submit: 'Criar usuário',
      success: (email) => `Usuário "${email}" criado.`,
    },
    cooldownModal: {
      title: 'Cooldown de device',
      description: (deviceIdPrefix) => `Bloqueia o device ${deviceIdPrefix}… temporariamente.`,
      confirmLabel: 'Aplicar cooldown',
      reasonLabel: 'Motivo (mín. 20 caracteres)',
      hoursLabel: 'Duração (horas, 1–720)',
      success: 'Device em cooldown.',
    },
    impersonateModal: {
      title: (email) => `Impersonar ${email}?`,
      description:
        'Ação registrada em auditoria. O token (TTL 30min) é copiado para a área de ' +
        'transferência ao confirmar — nunca fica na URL; o app exibe banner permanente ' +
        'de impersonation.',
      confirmLabel: 'Impersonar',
      reasonLabel: 'Motivo (mín. 10 caracteres)',
      success: (email) => `Impersonando ${email}. Token copiado — cole no app em até 30min.`,
      copyFailed: 'Não foi possível copiar o token para a área de transferência. Tente novamente.',
    },
    changeAccessModal: {
      blockTitle: (email) => `Bloquear ${email}?`,
      unblockTitle: (email) => `Desbloquear ${email}?`,
      blockDescription:
        'Impede novos acessos e encerra as sessões atuais. A saída pode levar alguns minutos em dispositivos já conectados.',
      unblockDescription: 'Permite novos acessos. O usuário precisará entrar novamente.',
      blockConfirm: 'Bloquear usuário',
      unblockConfirm: 'Desbloquear usuário',
      reasonLabel: 'Motivo (mín. 20 caracteres)',
      blockedSuccess: (revoked) =>
        revoked === 1
          ? 'Usuário bloqueado. 1 sessão encerrada.'
          : `Usuário bloqueado. ${revoked} sessões encerradas.`,
      alreadyBlocked: 'O usuário já estava bloqueado; o estado foi mantido.',
      unblockedSuccess: 'Usuário desbloqueado. É necessário fazer login novamente.',
      alreadyActive: 'O usuário já estava ativo; o estado foi mantido.',
    },
    issueLicenseModal: {
      title: 'Emitir license manual',
      description: 'Concede um entitlement manualmente. Sem data de expiração, vale por 90 dias.',
      packIdLabel: 'Pack ID',
      versionLabel: 'Versão',
      justificationLabel: 'Justificativa',
      kindComp: 'Cortesia',
      kindBeta: 'Beta',
      kindInternal: 'Interno',
      reasonLabel: 'Motivo (mín. 20 caracteres)',
      cancel: 'Cancelar',
      submit: 'Emitir license',
      success: (packId) => `License "${packId}" emitida.`,
    },
    revokeLicenseModal: {
      title: (packId) => `Revogar license "${packId}"?`,
      description:
        'Remove o entitlement do usuário. A instalação prévia segue, mas update/regeneração ' +
        'ficam bloqueados.',
      confirmLabel: 'Revogar license',
      reasonLabel: 'Motivo (mín. 10 caracteres)',
      success: (packId) => `License "${packId}" revogada.`,
    },
    revokeSessionModal: {
      title: 'Revogar sessão?',
      description: 'Encerra esta sessão. O dispositivo precisará entrar novamente.',
      confirmLabel: 'Revogar sessão',
      reasonLabel: 'Motivo (mín. 10 caracteres)',
      success: 'Sessão revogada. O dispositivo precisa refazer login.',
    },
    transferLicenseModal: {
      title: (packId) => `Transferir license "${packId}"`,
      description:
        'Move o entitlement para outro usuário da mesma organização (cross-org é rejeitado).',
      confirmLabel: 'Transferir',
      reasonLabel: 'Motivo (mín. 10 caracteres)',
      destinationLabel: 'Destino',
      success: (packId, email) => `License "${packId}" transferida para ${email}.`,
    },
    errors: {
      notFound: 'Recurso não encontrado (usuário, license ou sessão).',
      notBlocked: 'Requisição inválida para esta operação.',
      emailTaken: 'Já existe um usuário com este e-mail.',
      unauthorized: 'Você não tem permissão para esta operação (ou sua sessão expirou).',
      network: 'Falha de conexão. Tente novamente.',
      default: 'Operação falhou. Tente novamente.',
      unexpected: 'Erro inesperado.',
    },
  },
  'en-US': {
    createModal: {
      title: 'New user',
      description:
        'Creates an already-verified app user account. There is no password: the user signs in ' +
        'through the app with a code sent by email (OTP).',
      emailLabel: 'Email',
      emailPlaceholder: 'user@example.com',
      nameLabel: 'Name (optional)',
      namePlaceholder: 'Display name',
      validationError:
        'Enter a valid email. The name, if filled in, must be at most 120 characters.',
      cancel: 'Cancel',
      submit: 'Create user',
      success: (email) => `User "${email}" created.`,
    },
    cooldownModal: {
      title: 'Device cooldown',
      description: (deviceIdPrefix) => `Temporarily blocks device ${deviceIdPrefix}…`,
      confirmLabel: 'Apply cooldown',
      reasonLabel: 'Reason (min. 20 characters)',
      hoursLabel: 'Duration (hours, 1–720)',
      success: 'Device is in cooldown.',
    },
    impersonateModal: {
      title: (email) => `Impersonate ${email}?`,
      description:
        'Logged to the audit trail. The token (30min TTL) is copied to the clipboard on ' +
        'confirm — it never goes into the URL; the app shows a persistent impersonation ' +
        'banner.',
      confirmLabel: 'Impersonate',
      reasonLabel: 'Reason (min. 10 characters)',
      success: (email) =>
        `Impersonating ${email}. Token copied — paste it in the app within 30min.`,
      copyFailed: 'Could not copy the token to the clipboard. Try again.',
    },
    changeAccessModal: {
      blockTitle: (email) => `Block ${email}?`,
      unblockTitle: (email) => `Unblock ${email}?`,
      blockDescription:
        'Prevents new sign-ins and ends current sessions. Signed-in devices may take a few minutes to sign out.',
      unblockDescription: 'Allows new sign-ins. The user will need to sign in again.',
      blockConfirm: 'Block user',
      unblockConfirm: 'Unblock user',
      reasonLabel: 'Reason (min. 20 characters)',
      blockedSuccess: (revoked) =>
        revoked === 1
          ? 'User blocked. 1 session ended.'
          : `User blocked. ${revoked} sessions ended.`,
      alreadyBlocked: 'The user was already blocked; the state was preserved.',
      unblockedSuccess: 'User unblocked. A new sign-in is required.',
      alreadyActive: 'The user was already active; the state was preserved.',
    },
    issueLicenseModal: {
      title: 'Issue manual license',
      description: 'Manually grants an entitlement. With no expiration date, it lasts 90 days.',
      packIdLabel: 'Pack ID',
      versionLabel: 'Version',
      justificationLabel: 'Justification',
      kindComp: 'Complimentary',
      kindBeta: 'Beta',
      kindInternal: 'Internal',
      reasonLabel: 'Reason (min. 20 characters)',
      cancel: 'Cancel',
      submit: 'Issue license',
      success: (packId) => `License "${packId}" issued.`,
    },
    revokeLicenseModal: {
      title: (packId) => `Revoke license "${packId}"?`,
      description:
        'Removes the entitlement from the user. The prior install remains, but updates/' +
        'regeneration are blocked.',
      confirmLabel: 'Revoke license',
      reasonLabel: 'Reason (min. 10 characters)',
      success: (packId) => `License "${packId}" revoked.`,
    },
    revokeSessionModal: {
      title: 'Revoke session?',
      description: 'Ends this session. The device will need to sign in again.',
      confirmLabel: 'Revoke session',
      reasonLabel: 'Reason (min. 10 characters)',
      success: 'Session revoked. The device needs to sign in again.',
    },
    transferLicenseModal: {
      title: (packId) => `Transfer license "${packId}"`,
      description:
        'Moves the entitlement to another user in the same organization (cross-org is rejected).',
      confirmLabel: 'Transfer',
      reasonLabel: 'Reason (min. 10 characters)',
      destinationLabel: 'Destination',
      success: (packId, email) => `License "${packId}" transferred to ${email}.`,
    },
    errors: {
      notFound: 'Resource not found (user, license or session).',
      notBlocked: 'Invalid request for this operation.',
      emailTaken: 'A user with this email already exists.',
      unauthorized: 'You do not have permission for this operation (or your session expired).',
      network: 'Connection failed. Try again.',
      default: 'Operation failed. Try again.',
      unexpected: 'Unexpected error.',
    },
  },
};
